import React from 'react';
import { BRAND_NAME } from '../../theme/brand';
import {
    Box,
    Card,
    CardBody,
    Heading,
    Text,
    VStack,
    HStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tag,
    TableContainer,
    Button,
    Alert,
    AlertDescription,
    Divider,
    Grid,
    Flex
} from '@chakra-ui/react';
import {
    FiCode,
    FiCheck
} from 'react-icons/fi';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-json';
import CommonUseCases from './CommonUseCases';

export const MessageTracking = () => {
    return (
        <VStack spacing={6} align="stretch" mt={4}>
            {/* Introduction */}
            <Card shadow="sm" bg="blue.50" borderColor="var(--brand-primary)" borderWidth={1}>
                <CardBody>
                    <Text fontSize={"2xl"} fontWeight={"semibold"}>Enable Event Tracking from Template Messages</Text>
                    <Text color="gray.700">
                        {BRAND_NAME} allows you to automatically track events when messages are sent through templates. When you create a new template, there's an "Event Capturing" section where you can configure events to be triggered whenever the template is used to send messages.
                    </Text>
                </CardBody>
            </Card>

            {/* Benefits Section */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Why Use Event Tracking?</Text>
                <Divider bg="#EAEAEA" mb={4} />
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    <Box className='box-style-card' shadow="md">
                        <Box >
                            <Heading size="sm" mb={2} color="var(--brand-primary)" fontWeight={"500"}>Automated Event Capture</Heading>
                            <Text color="gray.700">
                                Automatically create event records whenever a template message is sent, eliminating manual event logging.
                            </Text>
                        </Box>
                    </Box>
                    <Box className='box-style-card' shadow="md">
                        <Box >
                            <Heading size="sm" mb={2} color="var(--brand-primary)" fontWeight={"500"}>Customer Journey Tracking</Heading>
                            <Text color="gray.700">
                                Track the complete customer journey by linking message deliveries to specific events in your system.
                            </Text>
                        </Box>
                    </Box>
                    <Box className='box-style-card' shadow="md">
                        <Box >
                            <Heading size="sm" mb={2} color="var(--brand-primary)" fontWeight={"500"}>Workflow Automation</Heading>
                            <Text color="gray.700">
                                Trigger follow-up actions, analytics, and business rules based on message-generated events.
                            </Text>
                        </Box>
                    </Box>
                    <Box className='box-style-card' shadow="md">
                        <Box >
                            <Heading size="sm" mb={2} color="var(--brand-primary)" fontWeight={"500"}>Analytics & Insights</Heading>
                            <Text color="gray.700">
                                Gain deeper insights into customer behavior by analyzing events generated from message interactions.
                            </Text>
                        </Box>
                    </Box>
                </Grid>
            </Box>

            {/* Step-by-Step Guide */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Step-by-Step Guide</Text>
                <Divider bg="#EAEAEA" mb={6} />

                {/* Step 1 */}
                <Flex mb={10} gap={4}>
                    <Box w="40px" h="40px" borderRadius="full" bg="var(--brand-primary)" color="white" display="flex" alignItems="center" justifyContent="center" flexShrink={0} fontWeight="semibold">
                        1
                    </Box>
                    <Box flex={1}>
                        <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Navigate to Template Manager</Text>
                        <Text color="gray.700" mb={4}>
                            Go to your {BRAND_NAME} dashboard and click on <strong>"Template Manager"</strong> from the main navigation menu. Then click <strong>"Create New Template"</strong> to open the template creation interface.
                        </Text>
                        <Card shadow="sm" p={6} bg="gray.50" border="2px solid" borderColor="gray.200">
                            <Flex justify="space-between" align="center" mb={4}>
                                <Box>
                                    <Heading size="sm" mb={1}>Template Manager</Heading>
                                    <Text color="gray.600">Manage multi-channel message templates</Text>
                                </Box>
                                <Button bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }}>
                                    + Create New Template
                                </Button>
                            </Flex>
                            <Box textAlign="center" py={8} border="2px dashed" borderColor="gray.300" borderRadius="md" bg="white">
                                <Text color="gray.600">Your existing templates will be listed here</Text>
                            </Box>
                        </Card>
                    </Box>
                </Flex>

                {/* Step 2 */}
                <Flex mb={10} gap={4}>
                    <Box w="40px" h="40px" borderRadius="full" bg="var(--brand-primary)" color="white" display="flex" alignItems="center" justifyContent="center" flexShrink={0} fontWeight="semibold">
                        2
                    </Box>
                    <Box flex={1}>
                        <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Fill in Template Basic Information</Text>
                        <Text color="gray.700" mb={4}>
                            Complete the basic template information including Template Name, Customer selection, Template Type, and your Master Message Content with variables (using $variable_name syntax).
                        </Text>
                        <Card shadow="sm" p={6} border="2px solid" borderColor="gray.200">
                            <Heading size="sm" mb={4}>Basic Information</Heading>
                            <VStack spacing={4} align="stretch">
                                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                                    <Box>
                                        <Text mb={2} fontWeight="medium">Template Name *</Text>
                                        <Box border="1px" borderColor="gray.300" borderRadius="md" px={3} py={2} bg="gray.50">
                                            <Text color="gray.400">Enter template name</Text>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Text mb={2} fontWeight="medium">Customer *</Text>
                                        <Box border="1px" borderColor="gray.300" borderRadius="md" px={3} py={2} bg="gray.50">
                                            <Text color="gray.400">Select customer</Text>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Text mb={2} fontWeight="medium">Template Type *</Text>
                                        <Box border="1px" borderColor="gray.300" borderRadius="md" px={3} py={2} bg="gray.50">
                                            <Text color="gray.400">Select template type</Text>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Box>
                                    <Text mb={2} fontWeight="medium">Master Message Content</Text>
                                    <Box border="1px" borderColor="gray.300" borderRadius="md" p={3} bg="gray.50" minH="100px">
                                        <Text color="gray.400">Your OTP code is $otp_code. It expires in $expiry_time.</Text>
                                    </Box>
                                    <Text fontSize="xs" color="gray.600" mt={1}>Use $variable_name syntax for dynamic content</Text>
                                </Box>
                            </VStack>
                        </Card>
                    </Box>
                </Flex>

                {/* Step 3 */}
                <Flex mb={10} gap={4}>
                    <Box w="40px" h="40px" borderRadius="full" bg="var(--brand-primary)" color="white" display="flex" alignItems="center" justifyContent="center" flexShrink={0} fontWeight="semibold">
                        3
                    </Box>
                    <Box flex={1}>
                        <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Scroll to "Event Capturing" Section</Text>
                        <Text color="gray.700" mb={4}>
                            Scroll down past all channel configurations until you reach the <strong>"Event Capturing"</strong> section at the bottom of the template form.
                        </Text>
                        <Card shadow="sm" border="2px solid" borderColor="gray.200">
                            <CardBody p={6}>
                                <Flex align="center" gap={2} mb={2}>
                                    <Box as={FiCode} color="purple.600" />
                                    <Heading size="sm">Event Capturing</Heading>
                                </Flex>
                                <Text color="gray.600" mb={4}>
                                    Configure events to capture and track when this template is used
                                </Text>
                                <Flex justify="space-between" align="center" mb={4}>
                                    <Heading size="sm">Events</Heading>
                                    <Button variant="outline" size="sm">+ Add Event</Button>
                                </Flex>
                                <Box textAlign="center" py={8} border="2px dashed" borderColor="gray.300" borderRadius="md">
                                    <Box as={FiCode} fontSize="2xl" mx="auto" mb={2} opacity={0.5} />
                                    <Text color="gray.600">No events configured yet. Click "Add Event" to track template usage.</Text>
                                </Box>
                            </CardBody>
                        </Card>
                    </Box>
                </Flex>

                {/* Step 4 */}
                <Flex mb={10} gap={4}>
                    <Box w="40px" h="40px" borderRadius="full" bg="var(--brand-primary)" color="white" display="flex" alignItems="center" justifyContent="center" flexShrink={0} fontWeight="semibold">
                        4
                    </Box>
                    <Box flex={1}>
                        <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Click "Add Event" Button</Text>
                        <Text color="gray.700" mb={4}>
                            In the Event Capturing section, click the <strong>"Add Event"</strong> button to add a new event configuration.
                        </Text>
                        <Alert bg="blue.50" borderColor="blue.200" borderWidth={1} borderRadius={"md"}>
                            <AlertDescription color="blue.800">
                                💡 <strong>Tip:</strong> You can add multiple events to a single template to track different aspects of message delivery and customer behavior.
                            </AlertDescription>
                        </Alert>
                    </Box>
                </Flex>

                {/* Step 5 */}
                <Flex mb={10} gap={4}>
                    <Box w="40px" h="40px" borderRadius="full" bg="var(--brand-primary)" color="white" display="flex" alignItems="center" justifyContent="center" flexShrink={0} fontWeight="semibold">
                        5
                    </Box>
                    <Box flex={1}>
                        <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Configure Event Details</Text>
                        <Text color="gray.700" mb={4}>Fill in the event configuration fields:</Text>

                        <Card shadow="sm" mb={4}>
                            <CardBody p={0}>
                                <TableContainer bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                                     <Table variant="simple">
                                         <Thead bg="#f7f7f7">
                                            <Tr>
                                                <Th>Field</Th>
                                                <Th>Description</Th>
                                                <Th>Example</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            <Tr>
                                                <Td fontFamily="mono">Event Name *</Td>
                                                <Td>A unique identifier for this event. Use underscores for spaces.</Td>
                                                <Td fontFamily="mono" color="gray.600">OTP_Sent</Td>
                                            </Tr>
                                            <Tr>
                                                <Td fontFamily="mono">Dynamic Values</Td>
                                                <Td>Select template variables to capture as event properties.</Td>
                                                <Td color="gray.600">$otp_code, $user_name</Td>
                                            </Tr>
                                            <Tr>
                                                <Td fontFamily="mono">Fixed Value</Td>
                                                <Td>Optional static value to include with this event.</Td>
                                                <Td fontFamily="mono" color="gray.600">channel=whatsapp</Td>
                                            </Tr>
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </CardBody>
                        </Card>

                        <Card shadow="sm" border="2px solid" borderColor="gray.200" p={4}>
                            <VStack spacing={4} align="stretch">
                                <Flex justify="space-between" align="center" mb={4}>
                                    <Text fontWeight="medium">Event 1</Text>
                                    <Button variant="ghost" size="sm" colorScheme="red">Delete</Button>
                                </Flex>
                                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                                    <Box>
                                        <Text mb={2} fontWeight="medium">Event Name *</Text>
                                        <Box border="1px" borderColor="gray.300" borderRadius="md" px={3} py={2} bg="gray.50">
                                            <Text color="gray.500">e.g., OTP_Sent</Text>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Text mb={2} fontWeight="medium">Dynamic Values</Text>
                                        <Box border="1px" borderColor="gray.300" borderRadius="md" p={2} maxH="32" overflowY="auto" bg="white">
                                            <VStack spacing={2} align="start">
                                                <HStack>
                                                    <input type="checkbox" />
                                                    <Text fontFamily="mono">$otp_code</Text>
                                                </HStack>
                                                <HStack>
                                                    <input type="checkbox" />
                                                    <Text fontFamily="mono">$user_name</Text>
                                                </HStack>
                                            </VStack>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Text mb={2} fontWeight="medium">Fixed Value</Text>
                                        <Box border="1px" borderColor="gray.300" borderRadius="md" px={3} py={2} bg="gray.50">
                                            <Text color="gray.500">Enter fixed value</Text>
                                        </Box>
                                    </Box>
                                </Grid>
                            </VStack>
                        </Card>
                    </Box>
                </Flex>

                {/* Step 6 */}
                <Flex mb={10} gap={4}>
                    <Box w="40px" h="40px" borderRadius="full" bg="var(--brand-primary)" color="white" display="flex" alignItems="center" justifyContent="center" flexShrink={0} fontWeight="semibold">
                        6
                    </Box>
                    <Box flex={1}>
                        <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Save Template</Text>
                        <Text color="gray.700" mb={4}>
                            After configuring your event(s), click the <strong>"Save Template"</strong> button. Your template is now configured to automatically create events!
                        </Text>
                        <Card shadow="sm" bg="#f0fdf4" borderColor="green.200" borderWidth={2}>
                            <CardBody p={6}>
                                <Flex gap={4}>
                                    <Box w="48px" h="48px" borderRadius="full" bg="#00c951" color="white" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                                        <FiCheck size={24} />
                                    </Box>
                                    <Box flex={1}>
                                        <Heading size="sm" color="green.900" mb={2}>Template Saved Successfully!</Heading>
                                        <Text color="green.800">
                                            Your template has been created with event tracking enabled. Every time a message is sent using this template, the configured events will be automatically captured.
                                        </Text>
                                    </Box>
                                </Flex>
                            </CardBody>
                        </Card>
                    </Box>
                </Flex>
            </Box>

            {/* Best Practices */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Best Practices</Text>
                <Divider bg="#EAEAEA" mb={4} />
                <Box className='box-style-card' shadow="sm">
                    <Box >
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <Heading size="xs" fontWeight={500} mb={2} color="var(--brand-primary)">✓ Use Descriptive Event Names</Heading>
                                <Text color="gray.700">
                                    Choose event names that clearly describe the action, such as <Tag bg="gray.100" color="black">order_confirmation_sent</Tag>
                                </Text>
                            </Box>
                            <Box>
                                <Heading size="xs" fontWeight={500} mb={2} color="var(--brand-primary)">✓ Keep Event Names Consistent</Heading>
                                <Text color="gray.700">
                                    Use a consistent naming convention across all your templates (e.g., always use snake_case and past tense).
                                </Text>
                            </Box>
                            <Box>
                                <Heading size="xs" fontWeight={500} mb={2} color="var(--brand-primary)">✓ Include Relevant Properties</Heading>
                                <Text color="gray.700">
                                    Add event properties that will be useful for analytics and automation.
                                </Text>
                            </Box>
                            <Box>
                                <Heading size="xs" fontWeight={500} mb={2} color="var(--brand-primary)">✓ Test Before Going Live</Heading>
                                <Text color="gray.700">
                                    Send test messages and verify that events are being created correctly before using the template in production.
                                </Text>
                            </Box>
                        </VStack>
                    </Box>
                </Box>
            </Box>
            <CommonUseCases />
            {/* Important Notes */}
            <Card shadow="sm" bg="#fefce8" borderColor="#fff085" borderWidth={1}>
                <CardBody p={6}>
                    <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >📌 Important Notes</Text>
                    <VStack spacing={2} align="start" color="gray.700">
                        <HStack align="start">
                            <Text color="yellow.600">•</Text>
                            <Text>Events are created in real-time when messages are sent through the template.</Text>
                        </HStack>
                        <HStack align="start">
                            <Text color="yellow.600">•</Text>
                            <Text>Each message sent will generate one event with the configured event name and properties.</Text>
                        </HStack>
                        <HStack align="start">
                            <Text color="yellow.600">•</Text>
                            <Text>Event data can be viewed in the Customer Dashboard under the "Events Tracking" tab.</Text>
                        </HStack>
                        <HStack align="start">
                            <Text color="yellow.600">•</Text>
                            <Text>You can modify event configuration at any time by editing the template.</Text>
                        </HStack>
                    </VStack>
                </CardBody>
            </Card>
        </VStack>
    );
};